import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DeviceMobile, X, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import {
  listTeachersInstallStatus,
  countMissingPush,
} from "@/lib/teacherInstalls";

const SNOOZE_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Admin · dashboard nudge — "Ask teachers to install".
 *
 * Only renders when at least one teacher in the org hasn't enrolled a
 * push device yet. Dismissible per-admin for 7 days via localStorage
 * (admin profile id scopes the key so multiple admins don't share a
 * snooze). Clicking "Review" links to /admin/teacher-installs, which
 * shows the full list + per-teacher status.
 *
 * Rendered BETWEEN the stats grid and the KPI card grid on the admin
 * dashboard so it feels like an operational nudge, not a marketing
 * banner.
 */
export default function TeacherInstallNudgeCard() {
  const { profile } = useAuth();
  const [rows, setRows] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const snoozeKey = profile?.id
    ? `little-oak:teacher-install-nudge-dismissed-at:${profile.id}`
    : null;

  useEffect(() => {
    if (!snoozeKey) return;
    try {
      const ts = Number(window.localStorage.getItem(snoozeKey) || 0);
      if (ts && Date.now() - ts < SNOOZE_MS) setDismissed(true);
    } catch {
      /* ignore private browsing */
    }
  }, [snoozeKey]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await listTeachersInstallStatus();
      if (alive) setRows(data);
    })();
    return () => { alive = false; };
  }, []);

  // Hide during load, if dismissed, or if nothing actionable.
  if (rows === null || dismissed) return null;
  const pending = countMissingPush(rows);
  if (pending === 0) return null;

  const total = rows.length;
  const previewNames = rows
    .filter((r) => !r.has_push)
    .slice(0, 3)
    .map((r) => r.full_name || r.email?.split("@")[0] || "Teacher");

  const onDismiss = () => {
    try { window.localStorage.setItem(snoozeKey, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <section
      data-testid="admin-teacher-install-nudge"
      data-pending={pending}
      className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-white to-[#FAF6EF]/50 p-5 shadow-[0_2px_12px_rgba(20,22,26,0.04)]"
    >
      <button
        type="button"
        aria-label="Dismiss for 7 days"
        data-testid="admin-teacher-install-nudge-dismiss"
        onClick={onDismiss}
        className="absolute right-3 top-3 rounded-full p-1.5 text-neutral-500 transition hover:bg-white/60 hover:text-neutral-800"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex flex-col gap-4 pr-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-sm">
            <DeviceMobile size={20} weight="duotone" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Teacher mobile rollout
            </p>
            <p className="mt-0.5 font-heading text-lg font-semibold tracking-tight text-neutral-900">
              <span data-testid="admin-teacher-install-nudge-pending">{pending}</span>
              {" of "}
              {total}
              {" "}teachers haven't installed the app yet
            </p>
            <p className="mt-1 text-xs leading-relaxed text-neutral-600">
              {previewNames.length > 0 && (
                <>
                  Including{" "}
                  <strong className="font-semibold text-neutral-800">
                    {previewNames.join(", ")}
                  </strong>
                  {pending > previewNames.length
                    ? ` +${pending - previewNames.length} more. `
                    : ". "}
                </>
              )}
              Ask them to open the app in their phone browser and tap
              <strong> Add to Home Screen</strong> — they'll start getting push
              alerts for parent messages.
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Link
            to="/admin/teacher-installs"
            data-testid="admin-teacher-install-nudge-review"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-hover"
          >
            Review
            <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </div>

      {/* Inline per-org rollout progress hint — subtle but adds context. */}
      <div className="mt-4 flex items-center gap-2 text-[11px] text-neutral-500">
        <CheckCircle size={12} weight="duotone" className="text-primary-hover" />
        <span>
          <strong className="font-semibold text-neutral-700">
            {total - pending}
          </strong>
          {" "}already set up · snooze this card for 7 days with the X above.
        </span>
      </div>
    </section>
  );
}
